import { App } from "~lib/App";
import * as React from "react";
import { useState, createContext, useContext } from "react";
import { Users, Eye, Gamepad2, DoorOpen } from "lucide-react";
import {
  ConnectionWrapper,
  Loading,
  TimedOut,
  useWrappedConnection,
} from "~lib/ConnectionProvider";
import { AppName, Namespace } from "~shared/messageTypes";
import {
  BaseClient,
  BaseServer,
  PlayerJoinStateName,
  RoomInfo,
  UserInfo,
} from "~shared/namespace/Base";
import {
  Connection,
  DataFromType,
  SyncStage,
  TypedListener,
} from "~shared/Connection";
import { toast } from "react-toastify";

export enum ConnectionState {
  CONNECTING,
  READY,
  TIMED_OUT,
}

class BaseConnection extends ConnectionWrapper {
  private setConnectionState: (state: ConnectionState) => void;
  constructor(
    base: Connection,
    roomInfoHandler: TypedListener<BaseServer.Type["ROOM_INFO"]>,
    userInfoHandler: TypedListener<BaseServer.Type["USER_INFO"]>,
    setConnectionState: (state: ConnectionState) => void,
    messageHandler: TypedListener<BaseServer.Type["MESSAGE"]>,
    gameStartedHandler: TypedListener<BaseServer.Type["GAME_STARTED"]>
  ) {
    super(base);
    this.setConnectionState = setConnectionState;
    this.handleMessageWithType(BaseServer.TypeValue.ROOM_INFO, roomInfoHandler);
    this.handleMessageWithType(BaseServer.TypeValue.USER_INFO, userInfoHandler);
    this.handleMessageWithType(BaseServer.TypeValue.MESSAGE, messageHandler);
    this.handleMessageWithType(
      BaseServer.TypeValue.GAME_STARTED,
      gameStartedHandler
    );
  }

  async register(timeout: number = 5000) {
    await super.register(timeout);
    this.setConnectionState(ConnectionState.READY);
    return this;
  }

  namespace(): Namespace {
    return AppName.LOBBY;
  }

  joinGame() {
    this.sendMessageWithType(BaseClient.TypeValue.JOIN_GAME, void 0);
  }

  leaveGame() {
    this.sendMessageWithType(BaseClient.TypeValue.LEAVE_GAME, void 0);
  }

  registerStage(): SyncStage {
    return SyncStage.REGISTER_BASE;
  }
}

const ConnectionStateProvider = createContext<ConnectionState>(
  ConnectionState.CONNECTING
);
const UserInfoProvider = createContext<UserInfo | undefined>(undefined);
const RoomInfoProvider = createContext<RoomInfo | undefined>(undefined);
const GameStartTimeProvider = createContext<number>(Date.now());

export const useUserInfo = () => {
  return useContext(UserInfoProvider);
};

export const useRoomInfo = () => {
  return useContext(RoomInfoProvider);
};

export const useConnectionState = () => {
  return useContext(ConnectionStateProvider);
};

export const useLastGameStartTime = () => {
  return useContext(GameStartTimeProvider);
};

export default () => {
  const [roomInfo, setRoomInfo] = useState<RoomInfo>();
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [messages, setMessages] = useState<string[]>([]);
  const [lastGameStartTime, setLastGameStartTime] = useState<number>(
    Date.now()
  );
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.CONNECTING
  );
  const handleMessage = (message: DataFromType<BaseServer.Type["MESSAGE"]>) => {
    const { toast: doToast, history, text } = message;
    if (doToast) toast(text);
    if (history) setMessages([text, ...messages.slice(0, 99)]);
  };

  const connection = useWrappedConnection(
    true,
    BaseConnection,
    setRoomInfo,
    setUserInfo,
    setConnectionState,
    handleMessage,
    setLastGameStartTime
  );

  if (connectionState === ConnectionState.CONNECTING) return <Loading />;
  if (connectionState === ConnectionState.TIMED_OUT) return <TimedOut />;

  const currentPlayer = (roomInfo?.players ?? []).find(
    (x) => x.id === userInfo?.id
  );

  const spectators = (roomInfo?.players ?? []).filter(
    (x) =>
      x.joinState === PlayerJoinStateName.SPECTATOR ||
      x.joinState === PlayerJoinStateName.JOINING
  );
  const players = (roomInfo?.players ?? []).filter(
    (x) =>
      x.joinState === PlayerJoinStateName.IN_GAME ||
      x.joinState === PlayerJoinStateName.LEAVING
  );

  return (
    <div className="max-h-screen max-w-screen p-3 md:p-6 h-screen w-screen">
      <div className="mx-auto">
        <div className="card-container mb-4">
          <h1 className="mb-2 flex items-center justify-center">Poker</h1>
          <p className="text-lg text-center">
            Welcome,{" "}
            <span className="font-bold text-yellow-300">
              {currentPlayer?.name}
            </span>
            !
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-4">
            <div className="card-container">
              <h2 className="mb-3 flex items-center gap-2">
                <Users className="text-yellow-300" size={20} />
                Players {players.length > 0 && `(${players.length})`}
              </h2>
              <div className="space-y-1.5">
                {players.length === 0 ? (
                  <p className="text-gray-300 italic">
                    No players yet - be the first!
                  </p>
                ) : (
                  players.map((x) => (
                    <div
                      key={x.id}
                      className="player-card flex items-center justify-between"
                    >
                      <span
                        className={`font-semibold ${
                          x.name === currentPlayer?.name
                            ? "text-yellow-300"
                            : ""
                        }`}
                      >
                        {x.name}
                      </span>

                      {x.joinState === PlayerJoinStateName.LEAVING && (
                        <span className="status-badge bg-red-500/50 text-white animate-pulse">
                          Leaving
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="card-container">
              <h2 className="mb-3 flex items-center gap-2">
                <Eye className="text-blue-300" size={20} />
                Spectators {spectators.length > 0 && `(${spectators.length})`}
              </h2>
              <div className="space-y-1.5">
                {spectators.length === 0 ? (
                  <p className="text-gray-300 italic">No spectators</p>
                ) : (
                  spectators.map((x) => (
                    <div
                      key={x.id}
                      className={
                        "player-card flex items-center justify-between"
                      }
                    >
                      <span
                        className={`font-semibold ${
                          x.name === currentPlayer?.name ? "text-blue-300" : ""
                        }`}
                      >
                        {x.name}
                      </span>

                      {x.joinState === PlayerJoinStateName.JOINING && (
                        <span className="status-badge bg-blue-500/60 text-white animate-pulse">
                          Joining...
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="card-container">
              {roomInfo && currentPlayer && (
                <div className="space-y-2">
                  {(currentPlayer.joinState === PlayerJoinStateName.SPECTATOR ||
                    currentPlayer.joinState ===
                      PlayerJoinStateName.LEAVING) && (
                    <button
                      className="w-full"
                      onClick={() => {
                        connection?.joinGame();
                      }}
                    >
                      <Gamepad2 className="inline-block mr-2" size={20} />
                      {currentPlayer.joinState === PlayerJoinStateName.SPECTATOR
                        ? "Join Game"
                        : "Cancel Leave"}
                    </button>
                  )}
                  {(currentPlayer.joinState === PlayerJoinStateName.IN_GAME ||
                    currentPlayer.joinState ===
                      PlayerJoinStateName.JOINING) && (
                    <button
                      className="w-full destructive"
                      onClick={() => {
                        connection?.leaveGame();
                      }}
                    >
                      <DoorOpen className="inline-block mr-2" size={20} />
                      {currentPlayer.joinState === PlayerJoinStateName.IN_GAME
                        ? "Leave Game"
                        : "Cancel Join"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="card-container game-board min-h-[400px]">
              <GameStartTimeProvider value={lastGameStartTime}>
                <ConnectionStateProvider value={connectionState}>
                  <UserInfoProvider value={userInfo}>
                    <RoomInfoProvider value={roomInfo}>
                      <App app={roomInfo?.currentGame ?? AppName.LOBBY} />
                    </RoomInfoProvider>
                  </UserInfoProvider>
                </ConnectionStateProvider>
              </GameStartTimeProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
